import { FileSearch } from 'lucide-react'
import React, { useEffect, useRef } from 'react'
import { Container } from 'react-grid-system'

import HeaderSpacing from '~/shared/components/HeaderSpace/HeaderSpace'

import BlogFilterBar from './components/BlogFilterBar'
import BlogFilterTags from './components/BlogFilterTags'
import BlogItem from './components/BlogItem'
import BlogPagination from './components/BlogPagination'
import useBlogData from './hooks/useBlogData'
import styles from './styles.module.scss'

const Blog: React.FC = () => {
  const {
    filters,
    setFilters,
    sortOrder,
    setSortOrder,
    currentPage,
    setCurrentPage,
    paginatedPosts,
    totalPages,
    activeFilters,
    regions,
    topics,
    categories,
    totalPostCount,
  } = useBlogData()

  const hasNoPostsYet = totalPostCount === 0
  const blogContentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentPage > 1 && blogContentRef.current) {
      blogContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [currentPage])

  return (
    <>
      <HeaderSpacing />
      <section className={`${styles.hero} ${hasNoPostsYet ? styles.heroNoPosts : ''}`}>
        <Container>
          <h1 className={styles.heroTitle}>Insights</h1>
          {!hasNoPostsYet && (
            <BlogFilterBar
              filters={filters}
              onFiltersChange={setFilters}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              regions={regions}
              topics={topics}
              categories={categories}
            />
          )}
        </Container>
      </section>
      {!hasNoPostsYet && activeFilters.length > 0 && (
        <div className={styles.tagsSection}>
          <Container>
            <BlogFilterTags
              activeFilters={activeFilters}
              onRemove={(key) => setFilters({ ...filters, [key]: null })}
              onClearAll={() =>
                setFilters({
                  date: null,
                  region: null,
                  topic: null,
                  category: null,
                })
              }
            />
          </Container>
        </div>
      )}
      <div
        ref={blogContentRef}
        className={`${styles.blogContent} ${hasNoPostsYet ? styles.blogContentNoPosts : ''}`}
      >
        <Container>
          {paginatedPosts.length > 0 ? (
            <>
              <div className={styles.blogList}>
                {paginatedPosts.map((post) => (
                  <BlogItem key={post.slug} slug={post.slug} frontmatter={post.frontmatter} />
                ))}
              </div>
              <BlogPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
                onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                onPageChange={setCurrentPage}
              />
            </>
          ) : hasNoPostsYet ? (
            <div className={styles.blogNoPostsLayout}>
              <div className={styles.blogUnderConstruction} role="status" aria-live="polite">
                <h2 className={styles.blogUnderConstructionTitle}>Stay tuned.</h2>
                <p className={styles.blogUnderConstructionLead}>
                  Our Insights page is currently under construction.
                </p>
              </div>
            </div>
          ) : (
            <div className={styles.blogEmptyState}>
              <FileSearch size={48} strokeWidth={1.25} aria-hidden />
              <p className={styles.blogEmptyStateText}>No posts match your filters.</p>
              <p className={styles.blogEmptyStateHint}>Try changing or clearing filters.</p>
            </div>
          )}
        </Container>
      </div>
    </>
  )
}

export default Blog
